<template>
    <div>
        <Card>
            <template #title>
                Exportação Consolidada
            </template>
            <template #content>
                <p class="text-sm text-color-secondary mb-4">
                    Gera planilha com dados de contratos, pagamentos, circuito/INEP e status. Aplique os filtros antes de exportar.
                </p>

                <form @submit.prevent="exportar">
                    <div class="grid">
                        <div class="col-12 md:col-4">
                            <FloatLabel class="w-full">
                                <Select id="modulo" v-model="form.modulo" :options="moduloOptions" optionLabel="label"
                                    optionValue="value" class="w-full" />
                                <label for="modulo">Módulo</label>
                            </FloatLabel>
                        </div>

                        <div class="col-12 md:col-4">
                            <FloatLabel class="w-full">
                                <Select id="etapa" v-model="form.etapa" :options="etapaOptions" optionLabel="label"
                                    optionValue="value" class="w-full" showClear />
                                <label for="etapa">Status / Etapa (contratos)</label>
                            </FloatLabel>
                        </div>

                        <div class="col-12 md:col-4">
                            <FloatLabel class="w-full">
                                <Select id="status_pagamento" v-model="form.status_pagamento" :options="statusPagamentoOptions"
                                    optionLabel="label" optionValue="value" class="w-full" showClear />
                                <label for="status_pagamento">Status pagamento</label>
                            </FloatLabel>
                        </div>

                        <div class="col-12 md:col-4">
                            <FloatLabel class="w-full">
                                <DatePicker id="periodo_inicio" v-model="form.periodo_inicio" showIcon class="w-full"
                                    dateFormat="dd/mm/yy" />
                                <label for="periodo_inicio">Período início</label>
                            </FloatLabel>
                        </div>

                        <div class="col-12 md:col-4">
                            <FloatLabel class="w-full">
                                <DatePicker id="periodo_fim" v-model="form.periodo_fim" showIcon class="w-full"
                                    dateFormat="dd/mm/yy" />
                                <label for="periodo_fim">Período fim</label>
                            </FloatLabel>
                        </div>

                        <div class="col-12">
                            <FloatLabel class="w-full">
                                <AutoComplete id="cliente" v-model="selectedCliente" :suggestions="clienteSuggestions"
                                    optionLabel="nome" class="w-full" @complete="searchCliente" @item-select="onSelectCliente"
                                    @clear="onClearCliente" dropdown forceSelection />
                                <label for="cliente">Cliente</label>
                            </FloatLabel>
                        </div>
                    </div>

                    <div class="flex justify-content-end gap-2 mt-4">
                        <Button type="button" label="Limpar filtros" severity="secondary" size="small" @click="limpar" />
                        <Button type="submit" icon="pi pi-download" label="Exportar" severity="success" size="small"
                            :loading="isLoading" />
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>

<script>
import Card from 'primevue/card';
import Button from 'primevue/button';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import DatePicker from 'primevue/datepicker';
import AutoComplete from 'primevue/autocomplete';

import ExportacaoConsolidada from '@/service/exportacaoConsolidada.js';
import Cliente from '@/service/cliente.js';
import File from '@/utils/File.js';
import Moment from 'moment';

export default {
    components: {
        Card,
        Button,
        Select,
        FloatLabel,
        DatePicker,
        AutoComplete,
    },
    data() {
        return {
            permissoes: permissoes,
            isLoading: false,
            moduloOptions: [
                { label: 'Pagamentos (Contas)', value: 'pagamentos' },
                { label: 'Contratos', value: 'contratos' },
                { label: 'Completo (2 abas Excel)', value: 'completo' },
            ],
            statusPagamentoOptions: [
                { label: 'Pago', value: 'pago' },
                { label: 'Pendente', value: 'pendente' },
            ],
            etapaOptions: [],
            selectedCliente: null,
            clienteSuggestions: [],
            form: {
                modulo: 'pagamentos',
                etapa: null,
                status_pagamento: null,
                periodo_inicio: null,
                periodo_fim: null,
                cliente_id: null,
            },
        };
    },
    beforeMount() {
        document.addEventListener('onPermissios', event => { this.permissoes = event.permissoes; });
        this.etapaOptions = (constantes?.ETAPA_OPTIONS || []).map(item => ({
            label: item.descricao || item.label || item.id,
            value: item.id,
        }));
    },
    mounted() {
        this.aplicarQueryInicial();
    },
    methods: {
        aplicarQueryInicial() {
            const q = this.$route.query;
            if (q.modulo) {
                this.form.modulo = q.modulo;
            }
        },
        searchCliente(event) {
            Cliente.list(`filter[nome]=${encodeURIComponent(event.query)}`)
                .then(response => {
                    this.clienteSuggestions = response.data.data || [];
                });
        },
        onSelectCliente(event) {
            this.form.cliente_id = event.value?.id || null;
        },
        onClearCliente() {
            this.selectedCliente = null;
            this.form.cliente_id = null;
        },
        limpar() {
            this.selectedCliente = null;
            this.form = {
                modulo: 'pagamentos',
                etapa: null,
                status_pagamento: null,
                periodo_inicio: null,
                periodo_fim: null,
                cliente_id: null,
            };
        },
        buildParams() {
            const params = [];
            params.push(`modulo=${encodeURIComponent(this.form.modulo)}`);

            if (this.form.etapa) {
                params.push(`etapa=${encodeURIComponent(this.form.etapa)}`);
            }
            if (this.form.status_pagamento) {
                params.push(`status_pagamento=${encodeURIComponent(this.form.status_pagamento)}`);
            }
            if (this.form.cliente_id) {
                params.push(`cliente_id=${encodeURIComponent(this.form.cliente_id)}`);
            }
            if (this.form.periodo_inicio) {
                params.push(`periodo_inicio=${Moment(this.form.periodo_inicio).format('YYYY-MM-DD')}`);
            }
            if (this.form.periodo_fim) {
                params.push(`periodo_fim=${Moment(this.form.periodo_fim).format('YYYY-MM-DD')}`);
            }

            return params.join('&');
        },
        exportar() {
            this.isLoading = true;

            ExportacaoConsolidada.download(this.buildParams())
                .then(response => {
                    File.downloadBlob(response);
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Exportação',
                        detail: 'Arquivo gerado com sucesso.',
                        life: 4000,
                    });
                })
                .catch(error => {
                    if (error.response?.data instanceof Blob) {
                        error.response.data.text().then(text => {
                            try {
                                const json = JSON.parse(text);
                                this.$toast.add({ severity: 'error', summary: 'Ops!', detail: json.msg || json.message, life: 6000 });
                            } catch (e) {
                                this.$toast.add({ severity: 'error', summary: 'Ops!', detail: 'Falha na exportação.', life: 5000 });
                            }
                        });
                        return;
                    }
                    const msg = error.response?.data?.msg || error.response?.data?.message || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 6000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>
